import React from "react";
import "./ConfirmTransferModal.scoped.css";

/**
 * ConfirmTransferModal
 *
 * Displays a summary of the transfer details for the user to review
 * before the transfer is submitted. Controlled by the `show_confirmation_page`
 * boolean in the FI's branding config.
 *
 * Props:
 *   transferSummary - object containing:
 *     recipient        - display string for the recipient
 *     amount           - dollar amount string (e.g. "25.00")
 *     note             - memo / note string
 *     fromLabel        - label for the source (e.g. "Checking ••••1234" or card nickname)
 *     isScheduled      - bool, true for S+R transfers
 *     scheduledDate    - optional formatted date string for scheduled transfers
 *     repeatFrequency  - optional repeat frequency label (e.g. "Weekly", "Monthly", "Never")
 *   onConfirm          - callback when user confirms
 *   onCancel           - callback when user cancels / goes back
 */
const ConfirmTransferModal = ({ transferSummary, onConfirm, onCancel }) => {
  const {
    recipient,
    amount,
    note,
    fromLabel,
    isScheduled,
    scheduledDate,
    repeatFrequency,
  } = transferSummary;

  return (
    <div
      className="legacy-confirm-transfer-modal-overlay"
      onClick={onCancel}
      data-cy="legacy-confirm-transfer-overlay"
    >
      <div
        className="legacy-confirm-transfer-modal"
        onClick={(e) => e.stopPropagation()}
        data-cy="legacy-confirm-transfer-modal"
      >
        {/* Header */}
        <div className="legacy-confirm-transfer-header">
          <h2>Confirm Transfer</h2>

        </div>

        {/* Details */}
        <div className="legacy-confirm-transfer-content">
          <div className="legacy-confirm-transfer-row">
            <span className="legacy-confirm-transfer-label">Amount</span>
            <span
              className="legacy-confirm-transfer-value legacy-confirm-transfer-amount"
              data-cy="confirm-modal-amount"
            >
              ${amount}
            </span>
          </div>

          <hr className="legacy-confirm-transfer-divider" />

          <div className="legacy-confirm-transfer-row">
            <span className="legacy-confirm-transfer-label">Recipient</span>
            <span
              className="legacy-confirm-transfer-value"
              data-cy="confirm-modal-recipient"
            >
              {recipient}
            </span>
          </div>

          <div className="legacy-confirm-transfer-row">
            <span className="legacy-confirm-transfer-label">From</span>
            <span className="legacy-confirm-transfer-value" data-cy="confirm-modal-from">
              {fromLabel}
            </span>
          </div>

          {note ? (
            <div className="legacy-confirm-transfer-row">
              <span className="legacy-confirm-transfer-label">Note</span>
              <span className="legacy-confirm-transfer-value" data-cy="confirm-modal-note">
                {note}
              </span>
            </div>
          ) : null}

          {isScheduled && scheduledDate ? (
            <div className="legacy-confirm-transfer-row">
              <span className="legacy-confirm-transfer-label">Scheduled Date</span>
              <span className="legacy-confirm-transfer-value" data-cy="confirm-modal-date">
                {scheduledDate}
              </span>
            </div>
          ) : null}

          {isScheduled && repeatFrequency && repeatFrequency !== "NEVER" ? (
            <div className="legacy-confirm-transfer-row">
              <span className="legacy-confirm-transfer-label">Repeats</span>
              <span className="legacy-confirm-transfer-value" data-cy="confirm-modal-recurrence">
                {repeatFrequency}
              </span>
            </div>
          ) : null}
        </div>

        {/* Action buttons */}
        <div className="legacy-confirm-transfer-footer">
          <button
            onClick={onConfirm}
            data-cy="confirm-modal-submit"
          >
            {isScheduled ? "Confirm Scheduled Transfer" : "Confirm & Send"}
          </button>
          <button
            className="legacy-confirm-modal-back-btn"
            onClick={onCancel}
            data-cy="confirm-modal-cancel"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmTransferModal;
